// frontend/modules/template/hooks/useTemplates.ts
import { useEffect, useCallback } from 'react';
import { useTemplateStore } from '../stores/template-store';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { useTheme } from '@/components/common/ThemeProvider';
import {
    Languages,
    NotificationPlatforms,
    TemplateContentType,
    MultiLanguageTemplateDTO
} from '@/shared/api/model';

// Import the Orval generated API client
import {
    useGetAllTextTemplates,
    useGetByName,
    useCreateMultiLanguage,
    useUpdateMultiLanguage,
    useDeleteAllLanguages
} from '@/shared/api/client/text-template-controller';

export const useTemplates = () => {
    const { dark } = useTheme();
    const {
        templates,
        filteredTemplates,
        isLoading,
        error,
        searchTerm,
        selectedPlatform,
        activeTemplateName,
        activePlatform,
        activeContentType,
        activeContents,
        activeSubjects,
        isEditMode,

        setTemplates,
        setIsLoading,
        setError,
        setSearchTerm,
        setSelectedPlatform,
        setActiveTemplate,
        setActiveContent,
        setActiveSubject,
        resetActiveTemplate,
        filterTemplates
    } = useTemplateStore();

    // Get text templates query
    const {
        data: templatesData,
        isLoading: isTemplatesLoading,
        refetch: refetchTemplates,
        error: templatesError
    } = useGetAllTextTemplates();

    // Create multi-language template mutation
    const { mutateAsync: createTemplate, isPending: isCreating } = useCreateMultiLanguage();

    // Update multi-language template mutation
    const { mutateAsync: updateTemplate, isPending: isUpdating } = useUpdateMultiLanguage();

    // Delete template mutation
    const { mutateAsync: deleteAllLanguages, isPending: isDeleting } = useDeleteAllLanguages();

    // Update templates when data is loaded
    useEffect(() => {
        if (templatesData) {
            setTemplates(templatesData);
            setError(null);
        }
    }, [templatesData, setTemplates, setError]);

    // Handle error state
    useEffect(() => {
        if (templatesError) {
            console.error('Error loading templates:', templatesError);
            setError('Failed to load templates');
            showErrorNotification('Error', 'Failed to load templates', dark);
        }
    }, [templatesError, setError, dark]);

    // Set overall loading state
    useEffect(() => {
        setIsLoading(isTemplatesLoading || isCreating || isUpdating || isDeleting);
    }, [isTemplatesLoading, isCreating, isUpdating, isDeleting, setIsLoading]);

    // Load templates
    const loadTemplates = useCallback(() => {
        refetchTemplates();
    }, [refetchTemplates]);

    // Export templates to JSON file
    const exportTemplates = useCallback(() => {
        // Create and download JSON file with templates
        try {
            const templatesJson = JSON.stringify(templates, null, 2);
            const blob = new Blob([templatesJson], { type: 'application/json' });
            const href = URL.createObjectURL(blob);

            // Create download link and trigger click
            const link = document.createElement('a');
            link.href = href;
            link.download = `templates_export_${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            URL.revokeObjectURL(href);

            showSuccessNotification('Success', 'Templates exported successfully', dark);
        } catch (err) {
            console.error('Error exporting templates:', err);
            showErrorNotification('Error', 'Failed to export templates', dark);
        }
    }, [templates, dark]);

    // Import templates from JSON file
    const importTemplates = useCallback(async (file: File) => {
        try {
            // Read file contents
            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    // Parse JSON
                    const content = e.target?.result as string;
                    const importedTemplates = JSON.parse(content);

                    // Validate imported data
                    if (!Array.isArray(importedTemplates)) {
                        throw new Error('Invalid import file format. Expected an array of templates.');
                    }

                    // Group templates by name and platform for bulk creation
                    const templateGroups = new Map<string, Map<NotificationPlatforms, MultiLanguageTemplateDTO>>();

                    importedTemplates.forEach((template: any) => {
                        if (!template.name || !template.platform || !template.language || !template.contentType) {
                            console.warn('Skipping invalid template:', template);
                            return;
                        }

                        const key = template.name;
                        if (!templateGroups.has(key)) {
                            templateGroups.set(key, new Map());
                        }

                        const platformMap = templateGroups.get(key)!;
                        if (!platformMap.has(template.platform)) {
                            platformMap.set(template.platform, {
                                name: template.name,
                                platform: template.platform,
                                contentType: template.contentType,
                                contentByLanguage: {},
                                subjectByLanguage: {}
                            });
                        }

                        const multiTemplate = platformMap.get(template.platform)!;
                        // @ts-ignore
                        multiTemplate.contentByLanguage[template.language] = template.content;
                        
                        // Map subject field if it exists
                        if (template.subject) {
                            // @ts-ignore
                            multiTemplate.subjectByLanguage[template.language] = template.subject;
                        }
                    });

                    // Create/update templates
                    let successCount = 0;
                    let errorCount = 0;

                    for (const platformMap of templateGroups.values()) {
                        for (const templateData of platformMap.values()) {
                            try {
                                await createTemplate({data:templateData});
                                successCount++;
                            } catch (error) {
                                console.error('Error importing template:', templateData.name, error);
                                errorCount++;
                            }
                        }
                    }

                    // Refresh templates list
                    await refetchTemplates();

                    if (errorCount === 0) {
                        showSuccessNotification('Import Complete', `Successfully imported ${successCount} templates`, dark);
                    } else {
                        showSuccessNotification(
                            'Import Completed with Errors',
                            `Imported ${successCount} templates. Failed to import ${errorCount} templates.`,
                            dark
                        );
                    }
                } catch (error) {
                    console.error('Error processing import file:', error);
                    showErrorNotification('Import Error', 'Failed to process import file. Please check the file format.', dark);
                }
            };

            reader.readAsText(file);
        } catch (err) {
            console.error('Error importing templates:', err);
            showErrorNotification('Error', 'Failed to import templates', dark);
        }
    }, [createTemplate, refetchTemplates, dark]);


    // Create or update a template in multiple languages
    const saveMultiLanguageTemplate = useCallback(async () => {
        if (!activeTemplateName || !activePlatform || !activeContentType) {
            showErrorNotification('Error', 'Template information is incomplete', dark);
            return;
        }

        // Check if both languages have content
        if (!activeContents[Languages.EN] || !activeContents[Languages.SW]) {
            showErrorNotification('Error', 'Content is required for both English and Swahili', dark);
            return;
        }

        try {
            const templateData: MultiLanguageTemplateDTO = {
                name: activeTemplateName,
                platform: activePlatform,
                contentType: activeContentType,
                contentByLanguage: activeContents,
                subjectByLanguage: activeSubjects
            };

            if (isEditMode) {
                // Update existing template
                await updateTemplate({
                    name: activeTemplateName,
                    platform: activePlatform,
                    data: templateData
                });
                showSuccessNotification('Success', 'Template updated successfully', dark);
            } else {
                // Create new template
                await createTemplate({data:templateData});
                showSuccessNotification('Success', 'Template created successfully', dark);
            }

            // Reload templates
            await refetchTemplates();
            resetActiveTemplate();
        } catch (err) {
            console.error('Error saving template:', err);
            showErrorNotification('Error', 'Failed to save template', dark);
        }
    }, [
        activeTemplateName,
        activePlatform,
        activeContentType,
        activeContents,
        activeSubjects,
        isEditMode,
        updateTemplate,
        createTemplate,
        refetchTemplates,
        resetActiveTemplate,
        dark
    ]);

    // Delete a template in all languages
    const deleteTemplate = useCallback(async (name: string, platform: NotificationPlatforms) => {
        try {
            await deleteAllLanguages({ name, platform });
            showSuccessNotification('Success', 'Template deleted successfully', dark);
            await refetchTemplates();
        } catch (err) {
            console.error('Error deleting template:', err);
            showErrorNotification('Error', 'Failed to delete template', dark);
        }
    }, [deleteAllLanguages, refetchTemplates, dark]);

    // Open the edit modal for a template
    const editTemplate = useCallback((name: string, platform: NotificationPlatforms, contentType: TemplateContentType) => {
        // Find template to edit
        const templateEN = templates.find(t =>
            t.name === name &&
            t.platform === platform &&
            t.language === Languages.EN
        );

        const templateSW = templates.find(t =>
            t.name === name &&
            t.platform === platform &&
            t.language === Languages.SW
        );

        // Determine content type - use specified or from existing template
        const type = contentType || (templateEN?.contentType || templateSW?.contentType);

        // Set active template state for editing
        setActiveTemplate(name, platform, type);

        // Set content values from existing templates
        if (templateEN) {
            setActiveContent(Languages.EN, templateEN?.content ||'');
        }

        if (templateSW) {
            setActiveContent(Languages.SW, templateSW?.content || '');
        }
    }, [templates, setActiveTemplate, setActiveContent]);

    // Open the create modal
    const createNewTemplate = useCallback(() => {
        resetActiveTemplate();
        setActiveTemplate('', null, null);
    }, [resetActiveTemplate, setActiveTemplate]);

    return {
        // State
        templates,
        filteredTemplates,
        isLoading,
        error,
        searchTerm,
        selectedPlatform,
        activeTemplateName,
        activePlatform,
        activeContentType,
        activeContents,
        activeSubjects,
        isEditMode,

        // Actions
        setSearchTerm,
        setSelectedPlatform,
        setActiveTemplate,
        setActiveContent,
        setActiveSubject,
        resetActiveTemplate,
        loadTemplates,
        saveMultiLanguageTemplate,
        deleteTemplate,
        editTemplate,
        createNewTemplate,

        // Import/Export
        exportTemplates,
        importTemplates
    };
};